// First-level path segments that are app pages (not business identifiers).
// Keep in sync with pages/(auth), pages/(website), pages/account.
const RESERVED_FIRST_SEGMENTS = new Set([
  "sign-in",
  "mobile-verification",
  "complete",
  "contact-sales",
  "contact-us",
  "privacy-policy",
  "request-demo",
  "terms-and-conditions",
  "whats-bookforme",
  "account",
]);

export default defineNuxtRouteMiddleware(async (to) => {
  const { $i18n } = useNuxtApp();
  const locales = $i18n.locales.value.map((l: { code: string }) => l.code);

  const path = canonicalUrlPath(to.path);
  // The query and hash ride along untouched.
  const suffix = to.fullPath.slice(to.path.length);

  /**
   * The locale prefix is matched case-insensitively. `/AR/salon` used to miss
   * this test altogether, get treated as a business identifier called "AR",
   * and 404 on a storefront that was fine.
   */
  const prefixMatch = path.match(/^\/([a-zA-Z]{2})(?=\/|$)/);
  const prefix = prefixMatch?.[1].toLowerCase();

  // The URL already names a locale. Nothing to look up — but the prefix may
  // still be the wrong case, or the path may carry a doubled slash or a
  // trailing full stop, so it is sent to its canonical form first.
  if (prefix && locales.includes(prefix)) {
    const canonical = `/${prefix}${path.slice(prefixMatch![0].length)}`;

    if (canonical === to.path) {
      return;
    }

    /**
     * A 301 belongs here and nowhere else in this middleware: `/AR/x` always
     * means `/ar/x`, while the locale *choice* below depends on a cookie and
     * on the partner's setting, so caching that one in the browser would pin
     * a visitor to a language they never picked.
     */
    return navigateTo(`${canonical}${suffix}`, { redirectCode: 301 });
  }

  const pinned = useCookie<string | null>("storefront_locale_pinned").value;
  const defaultLocale = $i18n.locale.value;
  let redirectLocale: string
    = pinned && locales.includes(pinned) ? pinned : defaultLocale;

  // If the first path segment looks like a business identifier (not a known
  // app route), ask the API for the partner's configured storefront language
  // and use it — unless the visitor has already pinned a locale.
  const firstSegment = path.split("/").filter(Boolean)[0];
  if (!pinned && firstSegment && !RESERVED_FIRST_SEGMENTS.has(firstSegment)) {
    try {
      const { apiVersion } = useRuntimeConfig().public;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await $larafetch<any>(
        `/api/${apiVersion}/${firstSegment}`,
      );
      const partnerLang = response?.data?.business?.default_language as
        | string
        | undefined;
      if (partnerLang && locales.includes(partnerLang)) {
        redirectLocale = partnerLang;
      }
    }
    catch {
      // Unknown identifier or API error — fall back to defaultLocale.
    }
  }

  await clearError();
  return navigateTo(`/${redirectLocale}${path}${suffix}`);
});
